import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Tooltip,
  Chip,
  Checkbox,
  FormControlLabel,
  Stack,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  ReceiptLongRounded as ReceiptLongIcon,
  TaskAltRounded as SettledIcon,
  PictureAsPdfRounded as PdfIcon,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { usePawnMode } from "../../Context/PawnModeContext";
import {
  reportPageSx,
  reportFixedSx,
  reportResultsCardSx,
  reportTableSx,
} from "./reportLayout";
import ReportDateWindow from "./reportDateWindow";
import ReportSortControl, { DEFAULT_SORT, sortRows } from "./reportSort";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// ─── Blue/White Design Tokens (shared with PaymentScreen/PaymentReport) ──────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SURFACE = "#FFFFFF";
const SUCCESS = "#059669";

const TODAY = () => dayjs().format("YYYY-MM-DD");
// Reports open on the running month, not on today alone: the first of the
// current month through today. A single-day-today default made every register
// look empty first thing in the morning.
const MONTH_START = () => dayjs().startOf("month").format("YYYY-MM-DD");

// Government-approved official monthly interest cap (%). Loans ARE written
// above this rate, but an OFFICIAL report must never print the true rate — it
// prints the store's master rate instead (itself clamped to the cap in Pawn
// Configuration), and the cap itself if settings could not be read. Unofficial
// reports always print the actual rate. Mirrors OFFICIAL_RATE_CAP in
// paymentScreen/shared.js and on the backend.
const OFFICIAL_RATE_CAP = 1.16;

// ── Formatting helpers ──────────────────────────────────────────────────────
const fmtINR = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};
const fmtWeight = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 3,
    minimumFractionDigits: 3,
  });
};
// Metal rate per gram — usually a whole number, so trim trailing decimals.
const fmtRate = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return null;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};
// Join a set of distinct values (rates / purities) with " / ", or "—" if empty.
const joinDistinct = (list) => (list && list.length ? list.join(" / ") : "—");

// Right-aligned column types for the dynamic settlement table.
const numericTypes = new Set(["inr", "value", "percent"]);

// ─── Sorting ─────────────────────────────────────────────────────────────────
// "Amount" is the row's Total (principal + interest) — the figure the register
// is read for. The Summary layout is a per-day aggregate, so only the options
// that survive that grouping are mapped for it.
const SORT_OPTIONS = [
  { key: "date", label: "Date" },
  { key: "amount", label: "Amount" },
  { key: "customer", label: "Customer Name" },
  { key: "voucher", label: "Rect No" },
];
const ROW_SORT = {
  date: (r) => r.settledDate,
  amount: (r) => r.total,
  customer: (r) => r.customerName,
  voucher: (r) => r.rectNo,
};
const SUMMARY_SORT = {
  date: (s) => s.date,
  amount: (s) => s.amount,
  voucher: (s) => s.fromLoan,
};

// Cache key for per-loan enrichment. Voucher numbers repeat across the official
// and unofficial series, so the series has to be part of the key.
const detailKey = (voucher, status) => `${voucher}|${status ?? ""}`;

// Run an async mapper with bounded concurrency so enriching a wide result set
// doesn't fire hundreds of loan-detail requests at once.
async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = new Array(Math.min(limit, items.length))
    .fill(0)
    .map(async () => {
      while (cursor < items.length) {
        const idx = cursor++;
        results[idx] = await worker(items[idx], idx);
      }
    });
  await Promise.all(runners);
  return results;
}

// ─── Main Screen ─────────────────────────────────────────────────────────────
const SettlementReport = ({ embedded = false }) => {
  const { currentStoreInfo } = useContext(AutoTaskAuthContext) || {};
  const storeId = currentStoreInfo?.id;
  // Pawn mode: 1 = official, 2 = unofficial. Official mode never sees
  // unofficial (status 2) records; unofficial mode shows ALL records. The
  // filtering happens server-side via the `mode` query param.
  const { mode: pawnMode } = usePawnMode() || {};

  // ── Filter state ──────────────────────────────────────────────────────────
  // Opens in range mode on the running month (1st → today), like every other
  // pawn register. A caller (e.g. the dashboard's "Settled / Closed" tile,
  // which opens the year-to-date range) may seed the date window via
  // navigation state so the report opens already scoped to some other window.
  const { state: navState } = useLocation();
  const [dateMode, setDateMode] = useState(navState?.dateMode || "range");
  const [singleDate, setSingleDate] = useState(navState?.singleDate || TODAY());
  const [fromDate, setFromDate] = useState(navState?.fromDate || MONTH_START());
  const [toDate, setToDate] = useState(navState?.toDate || TODAY());
  const [customerSearch, setCustomerSearch] = useState("");
  const [ticketSearch, setTicketSearch] = useState("");

  // ── Header view toggles ─────────────────────────────────────────────────
  // Column toggles reshape the default table; `summary` / `floorWise` swap it
  // for a different layout. `includeReceipts` changes the DATA SOURCE (and so
  // re-fetches). `tc` / `bankPledge` / `auctioned` are placeholders — rendered
  // and state-tracked only, pending KT.
  const [hideTotal, setHideTotal] = useState(false); // totals shown by default
  const [showMobile, setShowMobile] = useState(false);
  const [showRate, setShowRate] = useState(false);
  const [showPurity, setShowPurity] = useState(false);
  const [summary, setSummary] = useState(false);
  const [floorWise, setFloorWise] = useState(false);
  const [includeReceipts, setIncludeReceipts] = useState(false);
  const [tc, setTc] = useState(false); // placeholder
  const [bankPledge, setBankPledge] = useState(false); // placeholder
  const [auctioned, setAuctioned] = useState(false); // placeholder

  // Row order chosen from the sort button beside View — client-side only.
  const [sort, setSort] = useState(DEFAULT_SORT);

  // "Show All" is derived: ticked when all three column toggles are on; clicking
  // it flips all three together (totals stay visible).
  const showAll = showMobile && showRate && showPurity;
  const toggleShowAll = () => {
    const next = !showAll;
    setShowMobile(next);
    setShowRate(next);
    setShowPurity(next);
  };

  // ── Data state ────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Per-loan enrichment (rate / purity / floor / tare), fetched lazily only when
  // a toggle that needs it is on. Cached by loan voucher + series (rect no alone
  // is ambiguous — the official and unofficial books number independently).
  const detailCacheRef = useRef(new Map()); // "voucher|status" -> detail
  const [detailMap, setDetailMap] = useState(new Map());
  const [floorMap, setFloorMap] = useState(new Map()); // floor_id -> name

  const showSnackbar = useCallback(
    (message, severity = "info") =>
      setSnackbar({ open: true, message, severity }),
    [],
  );

  // Fetch (once per store) the master interest % from pawn settings. In
  // official mode any actual rate above the 1.16% ceiling is displayed as this
  // master rate instead; unofficial mode always shows the actual rate.
  const masterIntRef = useRef(null);
  const loadMasterInt = useCallback(async () => {
    if (storeId == null || masterIntRef.current != null) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/settings/${storeId}`,
      );
      const v = parseFloat(res.data?.data?.master_int);
      if (!isNaN(v)) masterIntRef.current = v;
    } catch {
      // Non-fatal: rates fall back to the actual stored value.
    }
  }, [storeId]);

  // Fetch (once per store) the floor directory so the Floor-Wise Summary can
  // resolve each loan's floor_id to a readable floor name.
  const loadFloorMap = useCallback(async () => {
    if (storeId == null) return;
    if (floorMap.size > 0) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/jewel-master/floors/store/${storeId}`,
      );
      const list = res.data?.data || [];
      const map = new Map();
      list.forEach((f) => map.set(f.id, f.name));
      setFloorMap(map);
    } catch {
      // Non-fatal: floors fall back to a "Floor <id>" label.
    }
  }, [storeId, floorMap]);

  // Pull a loan's ornament totals + floor/tare from the loan-detail endpoint,
  // reduced to just what Show Rate / Show Purity / Floor-Wise need. Cached.
  const loadTicketDetail = useCallback(
    async (voucherNo, rowStatus) => {
      const empty = {
        rateDisplay: "—",
        purityDisplay: "—",
        gross: 0,
        floorId: null,
        tareWt: 0,
      };
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/payments/loan/${encodeURIComponent(
            voucherNo,
          )}`,
          // Official and unofficial vouchers are independent series and can
          // collide, so the row's OWN status pins the lookup to the right loan.
          { params: { store_id: storeId, status: rowStatus ?? undefined } },
        );
        if (!(res.data?.success && res.data?.statusCode === 200)) return empty;
        const ornaments = res.data.data?.ornaments_info || [];
        const loanInfo = res.data.data?.loan_info || {};
        const gross = ornaments.reduce(
          (s, o) => s + (parseFloat(o.item_gross) || 0),
          0,
        );
        const rateSet = [];
        const puritySet = [];
        ornaments.forEach((o) => {
          const r = fmtRate(o.metal_type_rate);
          if (r != null && !rateSet.includes(r)) rateSet.push(r);
          const pur = parseFloat(o.purity_percentage);
          if (!isNaN(pur)) {
            const label = `${pur}%`;
            if (!puritySet.includes(label)) puritySet.push(label);
          }
        });
        return {
          rateDisplay: joinDistinct(rateSet),
          purityDisplay: joinDistinct(puritySet),
          gross,
          floorId: loanInfo.floor_id ?? null,
          tareWt: parseFloat(loanInfo.tare_wt) || 0,
        };
      } catch {
        return empty;
      }
    },
    [storeId],
  );

  // ── Apply filters & build the report ──────────────────────────────────────
  const buildReport = useCallback(async () => {
    if (storeId == null) {
      showSnackbar("No store selected.", "warning");
      return;
    }

    if (dateMode === "range" && fromDate && toDate && fromDate > toDate) {
      showSnackbar("'From' date cannot be after 'To' date.", "warning");
      return;
    }

    setLoading(true);
    try {
      await loadMasterInt();

      const startDate = dateMode === "single" ? singleDate : fromDate;
      const endDate = dateMode === "single" ? singleDate : toDate;
      const wantUnofficial = Number(pawnMode) === 2;
      const masterInt = masterIntRef.current;
      // Official mode never prints a rate above the cap: an above-cap loan
      // shows the store's master interest %, or the cap itself when settings
      // are unavailable — it must NEVER fall through to the true rate.
      // Unofficial mode always shows the actual rate.
      const officialRate =
        masterInt != null && masterInt <= OFFICIAL_RATE_CAP
          ? masterInt
          : OFFICIAL_RATE_CAP;
      const displayRateOf = (actual) => {
        const a =
          actual != null && actual !== "" ? parseFloat(actual) : Number.NaN;
        if (isNaN(a)) return "";
        if (wantUnofficial) return a;
        return a > OFFICIAL_RATE_CAP ? officialRate : a;
      };

      let mapped;
      if (includeReceipts) {
        // ── Include Receipts ON: EVERY receipt (partial payments + the final
        // settlement receipt), windowed on the RECEIPT date. ──
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/receipts/store/${storeId}`,
          { params: { mode: wantUnofficial ? 2 : 1, startDate, endDate } },
        );
        const all = res.data?.data || [];
        const custQuery = customerSearch.trim().toLowerCase();
        const ticketQuery = ticketSearch.trim().toLowerCase();
        const filtered = all.filter((p) => {
          if (ticketQuery) {
            const ledger = (p.voucher_ref || "").toLowerCase();
            const rect = (p.voucher_no || "").toLowerCase();
            if (!ledger.includes(ticketQuery) && !rect.includes(ticketQuery))
              return false;
          }
          if (custQuery) {
            const name = (p.customer_name || "").toLowerCase();
            const mobile = (p.customer_mobile || "").toString().toLowerCase();
            if (!name.includes(custQuery) && !mobile.includes(custQuery))
              return false;
          }
          return true;
        });
        mapped = filtered.map((p) => {
          const principal = parseFloat(p.principal_reduction) || 0;
          const interest = parseFloat(p.interest_deducted) || 0;
          return {
            id: p.id,
            settledDate: p.receipt_date,
            ledgerNo: p.voucher_ref || null,
            rectNo: p.voucher_no || null,
            pledgeDate: p.loan_date,
            customerName: p.customer_name || "—",
            mobile: p.customer_mobile != null ? String(p.customer_mobile) : "—",
            address: p.customer_address || "—",
            principal,
            interest,
            total: principal + interest,
            interestRate: displayRateOf(p.loan_interest),
            // The row's own series, used to pin per-loan enrichment lookups.
            status: p.status,
          };
        });
      } else {
        // ── Include Receipts OFF (default): only settlement (closing) receipts,
        // i.e. settled loans + their "H" receipt, windowed on the settled date. ──
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/settlements/store/${storeId}`,
          { params: { mode: wantUnofficial ? 2 : 1, startDate, endDate } },
        );
        const all = res.data?.data || [];
        const custQuery = customerSearch.trim().toLowerCase();
        const ticketQuery = ticketSearch.trim().toLowerCase();
        const filtered = all.filter((p) => {
          if (ticketQuery) {
            const ledger = (p.ledger_no || "").toLowerCase();
            const rect = (p.rect_no || "").toLowerCase();
            if (!ledger.includes(ticketQuery) && !rect.includes(ticketQuery))
              return false;
          }
          if (custQuery) {
            const name = (p.customer_name || "").toLowerCase();
            const mobile = (p.customer_mobile || "").toString().toLowerCase();
            if (!name.includes(custQuery) && !mobile.includes(custQuery))
              return false;
          }
          return true;
        });
        mapped = filtered.map((p) => {
          // The register reports the LOAN, not the closing payment:
          // Amount = the principal originally disbursed, Interest = every
          // rupee of interest paid over the loan's life (first month prepaid
          // at disbursement + all receipts). The backend computes both; the
          // closing receipt's own cash is available as closing_* if needed.
          const principal =
            parseFloat(p.settlement_principal ?? p.loan_amount) || 0;
          const interest = parseFloat(p.settlement_interest) || 0;
          return {
            id: p.id,
            settledDate: p.settled_date,
            ledgerNo: p.ledger_no || null, // "H" settlement voucher
            rectNo: p.rect_no || null, // loan / pledge voucher
            pledgeDate: p.pledge_date,
            customerName: p.customer_name || "—",
            mobile: p.customer_mobile != null ? String(p.customer_mobile) : "—",
            address: p.customer_address || "—",
            principal,
            interest,
            total: principal + interest,
            interestRate: displayRateOf(p.loan_interest),
            // The row's own series, used to pin per-loan enrichment lookups.
            status: p.status,
          };
        });
      }

      // Order by date (oldest first), matching a register read.
      mapped.sort((a, b) => {
        const d = (a.settledDate || "").localeCompare(b.settledDate || "");
        return d !== 0 ? d : (a.id || 0) - (b.id || 0);
      });
      setRows(mapped);
      if (mapped.length === 0) {
        showSnackbar(
          includeReceipts
            ? "No receipts found for the selected filters."
            : "No settlements found for the selected filters.",
          "info",
        );
      }
    } catch (err) {
      showSnackbar(
        err?.response?.data?.statusMessage ||
          "Failed to load settlement report.",
        "error",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    storeId,
    pawnMode,
    dateMode,
    singleDate,
    fromDate,
    toDate,
    customerSearch,
    ticketSearch,
    includeReceipts,
    loadMasterInt,
    showSnackbar,
  ]);

  // Auto-load on first mount, when the store resolves, when the pawn mode flips,
  // whenever a DATE is picked, and whenever Include Receipts flips (it changes
  // the data source). Text fields load only on View/Enter.
  useEffect(() => {
    if (storeId != null) buildReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeId,
    pawnMode,
    dateMode,
    singleDate,
    fromDate,
    toDate,
    includeReceipts,
  ]);

  // Lazily enrich rows with ornament (rate/purity) + floor/tare data — only when
  // a toggle that needs it is on. Cached by loan voucher, bounded concurrency.
  const needEnrichment = showRate || showPurity || floorWise;
  useEffect(() => {
    if (!needEnrichment || storeId == null || rows.length === 0) return;
    let cancelled = false;
    (async () => {
      await loadFloorMap();
      const wanted = new Map(); // key -> { voucher, status }
      rows.forEach((r) => {
        if (!r.rectNo) return;
        const key = detailKey(r.rectNo, r.status);
        if (!detailCacheRef.current.has(key) && !wanted.has(key))
          wanted.set(key, { voucher: r.rectNo, status: r.status });
      });
      const missing = [...wanted.entries()];
      if (missing.length) {
        await mapWithConcurrency(
          missing,
          6,
          async ([key, { voucher, status }]) => {
            detailCacheRef.current.set(
              key,
              await loadTicketDetail(voucher, status),
            );
          },
        );
      }
      if (!cancelled) setDetailMap(new Map(detailCacheRef.current));
    })();
    return () => {
      cancelled = true;
    };
  }, [needEnrichment, rows, storeId, loadTicketDetail, loadFloorMap]);

  const handleReset = () => {
    setDateMode("range");
    setSingleDate(TODAY());
    setFromDate(MONTH_START());
    setToDate(TODAY());
    setCustomerSearch("");
    setTicketSearch("");
    // Also clear the view toggles so the report returns to its default layout.
    setHideTotal(false);
    setShowMobile(false);
    setShowRate(false);
    setShowPurity(false);
    setSummary(false);
    setFloorWise(false);
    setIncludeReceipts(false);
    setTc(false);
    setBankPledge(false);
    setAuctioned(false);
    setSort(DEFAULT_SORT);
  };

  // ── Rows merged with lazy enrichment (rate / purity) ────────────────────────
  const displayRows = useMemo(
    () =>
      sortRows(
        rows.map((r) => {
          const e = detailMap.get(detailKey(r.rectNo, r.status));
          const pending = needEnrichment && !e ? "…" : "—";
          return {
            ...r,
            rateDisplay: e ? e.rateDisplay : pending,
            purityDisplay: e ? e.purityDisplay : pending,
          };
        }),
        sort,
        ROW_SORT,
      ),
    [rows, detailMap, needEnrichment, sort],
  );

  // ── Totals ────────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.principal += r.principal || 0;
        acc.interest += r.interest || 0;
        acc.total += r.total || 0;
        return acc;
      },
      { principal: 0, interest: 0, total: 0 },
    );
  }, [rows]);

  // ── Dynamic column set for the default table ────────────────────────────────
  // Mobile / Rate / Purity are inserted only when their toggle is on; the
  // `total` key marks which columns carry a footer sum.
  const columns = useMemo(() => {
    const c = [
      { key: "settledDate", label: "Date", type: "date" },
      { key: "ledgerNo", label: "Ledger No.", type: "text", strong: true },
      { key: "rectNo", label: "Rect No", type: "text" },
      {
        key: "principal",
        label: "Amount (₹)",
        type: "inr",
        total: "principal",
        strong: true,
      },
    ];
    if (showMobile) c.push({ key: "mobile", label: "Mobile", type: "text" });
    c.push({ key: "customerName", label: "Name", type: "text", strong: true });
    c.push({ key: "address", label: "Address", type: "address" });
    c.push({ key: "pledgeDate", label: "Pledge Date", type: "date" });
    if (showRate) c.push({ key: "rateDisplay", label: "Rate", type: "value" });
    if (showPurity)
      c.push({ key: "purityDisplay", label: "Purity", type: "value" });
    c.push({ key: "interest", label: "Interest (₹)", type: "inr", total: "interest" });
    c.push({
      key: "total",
      label: "Total (₹)",
      type: "inr",
      total: "total",
      strong: true,
    });
    c.push({ key: "interestRate", label: "%", type: "percent" });
    return c;
  }, [showMobile, showRate, showPurity]);

  // ── Summary report: one row per day with the loan-voucher span + day total ──
  const summaryRows = useMemo(() => {
    const num = (v) => parseInt(String(v ?? "").replace(/\D/g, ""), 10);
    const byDate = new Map();
    rows.forEach((r) => {
      const d = r.settledDate;
      if (!byDate.has(d)) byDate.set(d, []);
      byDate.get(d).push(r);
    });
    return [...byDate.keys()].sort().map((d) => {
      const list = byDate.get(d);
      const sorted = [...list].sort(
        (a, b) => (num(a.rectNo) || 0) - (num(b.rectNo) || 0),
      );
      return {
        date: d,
        fromLoan: sorted[0]?.rectNo ?? "—",
        toLoan: sorted[sorted.length - 1]?.rectNo ?? "—",
        amount: list.reduce((s, r) => s + (r.principal || 0), 0),
      };
    });
  }, [rows]);

  const summaryTotal = useMemo(
    () => summaryRows.reduce((s, r) => s + r.amount, 0),
    [summaryRows],
  );

  // The per-day summary in the order the sort button asked for.
  const displaySummaryRows = useMemo(
    () => sortRows(summaryRows, sort, SUMMARY_SORT),
    [summaryRows, sort],
  );

  // ── Floor-Wise summary: aggregate the loans by floor ────────────────────────
  // Weight of Jewels = Σ gross; Total = Gross + Tare (no separate Extra Tare).
  const floorRows = useMemo(() => {
    const NO_FLOOR = "__none__";
    const byFloor = new Map();
    // Seed every floor in the store first, so floors with no matching loans in
    // the current result set still show up as a 0 row.
    floorMap.forEach((name, id) => {
      byFloor.set(String(id), { name, count: 0, gross: 0, tare: 0 });
    });
    rows.forEach((r) => {
      const e = detailMap.get(detailKey(r.rectNo, r.status));
      const floorId = e ? e.floorId : null;
      const key = floorId == null ? NO_FLOOR : String(floorId);
      if (!byFloor.has(key)) {
        byFloor.set(key, {
          name:
            floorId == null
              ? "— (No Floor)"
              : floorMap.get(floorId) || `Floor ${floorId}`,
          count: 0,
          gross: 0,
          tare: 0,
        });
      }
      const agg = byFloor.get(key);
      agg.count += 1;
      agg.gross += e ? e.gross || 0 : 0;
      agg.tare += e ? e.tareWt || 0 : 0;
    });
    return [...byFloor.values()].map((f) => ({ ...f, total: f.gross + f.tare }));
  }, [rows, detailMap, floorMap]);

  const floorTotals = useMemo(
    () =>
      floorRows.reduce(
        (acc, f) => {
          acc.count += f.count;
          acc.gross += f.gross;
          acc.tare += f.tare;
          acc.total += f.total;
          return acc;
        },
        { count: 0, gross: 0, tare: 0, total: 0 },
      ),
    [floorRows],
  );

  // Which report layout is active. Summary wins over Floor-Wise if both are on.
  const activeView = summary ? "summary" : floorWise ? "floor" : "list";
  const recordNoun = includeReceipts ? "receipt" : "settlement";

  // ── PDF export of the currently displayed report ───────────────────────────
  const generatePDF = () => {
    if (rows.length === 0) {
      showSnackbar(`Nothing to print. Load some ${recordNoun}s first.`, "warning");
      return;
    }

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;

    const periodLabel =
      dateMode === "single"
        ? dayjs(singleDate).format("DD-MM-YYYY")
        : `${dayjs(fromDate).format("DD-MM-YYYY")} to ${dayjs(toDate).format(
            "DD-MM-YYYY",
          )}`;

    const reportTitle =
      activeView === "summary"
        ? "Loan Settlements Summary"
        : activeView === "floor"
          ? "Floorwise Summary"
          : includeReceipts
            ? "Loan Settlements (incl. Receipts)"
            : "Loan Settlements";

    // Store name + address block (mirrors the printed ledger heading).
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(currentStoreInfo?.name || "Settlement Report", margin, 14);

    if (currentStoreInfo?.address) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(String(currentStoreInfo.address), margin, 19);
    }

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(`${reportTitle} on ${periodLabel}`, margin, 27);

    const filterBits = [];
    if (customerSearch.trim())
      filterBits.push(`Customer: ${customerSearch.trim()}`);
    if (ticketSearch.trim()) filterBits.push(`Ledger: ${ticketSearch.trim()}`);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${rows.length} ${recordNoun}${rows.length === 1 ? "" : "s"}${
        filterBits.length ? ` | ${filterBits.join(" | ")}` : ""
      } | Generated ${dayjs().format("DD-MM-YYYY HH:mm")}`,
      pageWidth - margin,
      27,
      { align: "right" },
    );

    // A cell value for the PDF (mirrors the on-screen formatCell but uses "-").
    const pdfVal = (col, r) => {
      const v = r[col.key];
      switch (col.type) {
        case "date":
          return v ? dayjs(v).format("DD-MM-YY") : "-";
        case "inr":
          return v == null ? "-" : fmtINR(v);
        case "percent":
          return v === "" || v == null ? "-" : `${v}`;
        default:
          return v == null || v === "—" ? "-" : String(v);
      }
    };

    let head, body, colStyles;

    if (activeView === "summary") {
      head = [
        ["Sl No", "Date", "From Loan No", "To Loan No", "Amount (Rs)"],
      ];
      body = displaySummaryRows.map((s, idx) => [
        idx + 1,
        s.date ? dayjs(s.date).format("DD-MM-YYYY") : "-",
        s.fromLoan,
        s.toLoan,
        fmtINR(s.amount),
      ]);
      if (!hideTotal)
        body.push([
          { content: "Total", colSpan: 4, styles: { fontStyle: "bold" } },
          {
            content: fmtINR(summaryTotal),
            styles: { fontStyle: "bold", halign: "right" },
          },
        ]);
      colStyles = {
        0: { halign: "center", cellWidth: 16 },
        4: { halign: "right" },
      };
    } else if (activeView === "floor") {
      head = [
        [
          "Floor",
          "No of Pawn Ticket",
          "Weight of Jewels (g)",
          "Tare Wt (g)",
          "Extra Tare (g)",
          "Total Wt (g)",
        ],
      ];
      body = floorRows.map((f) => [
        f.name,
        f.count,
        fmtWeight(f.gross),
        fmtWeight(f.tare),
        "-",
        fmtWeight(f.total),
      ]);
      if (!hideTotal)
        body.push([
          { content: "Total", styles: { fontStyle: "bold" } },
          {
            content: floorTotals.count,
            styles: { fontStyle: "bold", halign: "right" },
          },
          {
            content: fmtWeight(floorTotals.gross),
            styles: { fontStyle: "bold", halign: "right" },
          },
          {
            content: fmtWeight(floorTotals.tare),
            styles: { fontStyle: "bold", halign: "right" },
          },
          { content: "-", styles: { fontStyle: "bold", halign: "right" } },
          {
            content: fmtWeight(floorTotals.total),
            styles: { fontStyle: "bold", halign: "right" },
          },
        ]);
      colStyles = {
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      };
    } else {
      head = [columns.map((c) => c.label.replace("₹", "Rs"))];
      body = displayRows.map((r) => columns.map((c) => pdfVal(c, r)));
      if (!hideTotal) {
        const totalRow = columns.map((c) =>
          c.total ? fmtINR(totals[c.total]) : "",
        );
        totalRow[0] = "Totals";
        body.push(
          totalRow.map((content, i) => ({
            content,
            styles: {
              fontStyle: "bold",
              halign: numericTypes.has(columns[i].type) ? "right" : "left",
            },
          })),
        );
      }
      colStyles = {};
      columns.forEach((c, i) => {
        colStyles[i] = { halign: numericTypes.has(c.type) ? "right" : "left" };
      });
    }

    doc.autoTable({
      startY: 32,
      head,
      body,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        textColor: [15, 23, 42],
        valign: "middle",
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8,
        halign: "center",
        valign: "middle",
      },
      columnStyles: colStyles,
      margin: { left: margin, right: margin },
    });

    const fileLabel =
      dateMode === "single"
        ? dayjs(singleDate).format("YYYY-MM-DD")
        : `${dayjs(fromDate).format("YYYY-MM-DD")}_to_${dayjs(toDate).format(
            "YYYY-MM-DD",
          )}`;
    doc.save(`Settlement_Report_${fileLabel}.pdf`);
  };

  // ── Cell styling ────────────────────────────────────────────────────────────
  const headCellSx = {
    bgcolor: "#F8FAFC",
    color: INK_MUTED,
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    borderBottom: `1px solid ${BORDER}`,
    whiteSpace: "nowrap",
  };
  const cellSx = {
    fontSize: "0.8rem",
    color: INK_SOFT,
    borderBottom: `1px solid ${BORDER}`,
    py: 1,
    verticalAlign: "middle",
  };
  const footBase = {
    position: "sticky",
    bottom: 0,
    bgcolor: "#F8FAFC",
    fontWeight: 800,
    color: INK,
    fontSize: "0.82rem",
    borderTop: `2px solid ${BORDER}`,
  };
  // Page stays viewport-height; the table takes the leftover height and scrolls
  // internally with the scrollbar hidden (shared convention — reportLayout.js).
  const tableContainerSx = reportTableSx;

  // Format a body cell for the dynamic list table by column type.
  const formatCell = (col, r) => {
    const v = r[col.key];
    switch (col.type) {
      case "date":
        return v ? dayjs(v).format("DD MMM YYYY") : "—";
      case "inr":
        return v == null ? "—" : fmtINR(v);
      case "percent":
        return v === "" || v == null ? "—" : `${v}`;
      default:
        return v ?? "—";
    }
  };

  return (
    <Box sx={reportPageSx(embedded)}>
      {/* ── Header (hidden when embedded in the unified Reports screen) ── */}
      {!embedded && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            ...reportFixedSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: BLUE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
            }}
          >
            <SettledIcon />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 800,
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                color: INK }}
            >
              Settlement Report
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: INK_MUTED }}>
              {currentStoreInfo?.name ? `${currentStoreInfo.name}` : ""}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Filters ── */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          mb: 2,
          bgcolor: SURFACE,
          ...reportFixedSx,
        }}
      >
        <CardContent
          sx={{
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.5, md: 2 },
            "&:last-child": { pb: { xs: 1.5, md: 2 } },
          }}
        >
          <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="flex-end">
            {/* Date window: "Single Day" ticked = one field; unticked slides
                a "To" field open beside it. */}
            <ReportDateWindow
              label={includeReceipts ? "Receipt Date" : "Settlement Date"}
              dateMode={dateMode}
              setDateMode={setDateMode}
              singleDate={singleDate}
              setSingleDate={setSingleDate}
              fromDate={fromDate}
              setFromDate={setFromDate}
              toDate={toDate}
              setToDate={setToDate}
            />

            {/* Customer search */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Customer"
                placeholder="Name or mobile"
                size="small"
                fullWidth
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buildReport()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 18, color: INK_MUTED }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Ledger no. search */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Ledger No."
                placeholder="Ledger / voucher no."
                size="small"
                fullWidth
                value={ticketSearch}
                onChange={(e) => setTicketSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buildReport()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ReceiptLongIcon
                        sx={{ fontSize: 18, color: INK_MUTED }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Actions */}
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={buildReport}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  sx={{
                    bgcolor: BLUE,
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
                  }}
                >
                  View
                </Button>
                <ReportSortControl
                  options={SORT_OPTIONS}
                  value={sort}
                  onChange={setSort}
                />
                <Tooltip title="Reset filters">
                  <IconButton
                    onClick={handleReset}
                    sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}
                  >
                    <RefreshIcon sx={{ color: INK_MUTED }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>

          {/* ── Report options (client-side view toggles) ── */}
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: `1px dashed ${BORDER}`,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 0.5,
              rowGap: 0.25,
            }}
          >
            {[
              {
                label: "Hide Total",
                checked: hideTotal,
                onChange: () => setHideTotal((v) => !v),
              },
              {
                label: "Show Mobile",
                checked: showMobile,
                onChange: () => setShowMobile((v) => !v),
              },
              {
                label: "Show Rate",
                checked: showRate,
                onChange: () => setShowRate((v) => !v),
              },
              {
                label: "Show Purity",
                checked: showPurity,
                onChange: () => setShowPurity((v) => !v),
              },
              { divider: true },
              {
                label: "Summary",
                checked: summary,
                onChange: () => setSummary((v) => !v),
              },
              {
                label: "Floor Wise Summary",
                checked: floorWise,
                onChange: () => setFloorWise((v) => !v),
              },
              { divider: true },
              {
                label: "Show All",
                checked: showAll,
                onChange: toggleShowAll,
              },
              {
                label: "Include Receipts",
                checked: includeReceipts,
                onChange: () => setIncludeReceipts((v) => !v),
              },
              { divider: true },
              {
                label: "T & C",
                checked: tc,
                onChange: () => setTc((v) => !v),
              },
              {
                label: "Bank Pledge",
                checked: bankPledge,
                onChange: () => setBankPledge((v) => !v),
              },
              {
                label: "Auctioned Settlement",
                checked: auctioned,
                onChange: () => setAuctioned((v) => !v),
              },
            ].map((opt, i) =>
              opt.divider ? (
                <Box
                  key={`div-${i}`}
                  sx={{
                    width: "1px",
                    alignSelf: "stretch",
                    my: 0.5,
                    mx: 0.75,
                    bgcolor: BORDER,
                  }}
                />
              ) : (
                <FormControlLabel
                  key={opt.label}
                  control={
                    <Checkbox
                      size="small"
                      checked={opt.checked}
                      onChange={opt.onChange}
                      sx={{
                        py: 0.25,
                        color: INK_MUTED,
                        "&.Mui-checked": { color: BLUE },
                      }}
                    />
                  }
                  label={opt.label}
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      color: INK_SOFT,
                    },
                  }}
                />
              ),
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ── View section (results) ── */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          bgcolor: SURFACE,
          ...reportResultsCardSx,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            borderBottom: `1px solid ${BORDER}`,
            ...reportFixedSx,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: INK, fontSize: "0.95rem" }}>
            {activeView === "summary"
              ? "Loan Settlements Summary"
              : activeView === "floor"
                ? "Floorwise Summary"
                : includeReceipts
                  ? "Loan Settlements (incl. Receipts)"
                  : "Loan Settlements"}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={`${rows.length} ${
                rows.length === 1 ? recordNoun : `${recordNoun}s`
              }`}
              size="small"
              sx={{
                bgcolor: BLUE_PALE,
                color: BLUE_DARK,
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={generatePDF}
              disabled={loading || rows.length === 0}
              startIcon={<PdfIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: BLUE,
                borderColor: BLUE,
                "&:hover": { borderColor: BLUE_DARK, bgcolor: BLUE_PALE },
              }}
            >
              Print PDF
            </Button>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: BLUE }} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
            <SettledIcon sx={{ fontSize: 44, color: BORDER, mb: 1 }} />
            <Typography sx={{ color: INK_MUTED, fontSize: "0.9rem" }}>
              No {recordNoun}s to show. Adjust the filters and press View.
            </Typography>
          </Box>
        ) : activeView === "summary" ? (
          /* ── Summary: per-day loan-voucher span + day total ── */
          <TableContainer sx={tableContainerSx}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Sl No</TableCell>
                  <TableCell sx={headCellSx}>Date</TableCell>
                  <TableCell sx={headCellSx}>From Loan No</TableCell>
                  <TableCell sx={headCellSx}>To Loan No</TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Amount (₹)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displaySummaryRows.map((s, idx) => (
                  <TableRow
                    key={s.date}
                    hover
                    sx={{ "&:hover": { bgcolor: BLUE_PALE } }}
                  >
                    <TableCell sx={{ ...cellSx, color: INK_MUTED }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                      {s.date ? dayjs(s.date).format("DD-MM-YYYY") : "—"}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 700, color: INK }}>
                      {s.fromLoan}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 700, color: INK }}>
                      {s.toLoan}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...cellSx, fontWeight: 600 }}
                    >
                      {fmtINR(s.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {!hideTotal && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={4} sx={footBase}>
                      Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...footBase, color: SUCCESS }}
                    >
                      {fmtINR(summaryTotal)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        ) : activeView === "floor" ? (
          /* ── Floor-Wise summary: loans aggregated by floor ── */
          <TableContainer sx={tableContainerSx}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Floor</TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    No of Pawn Ticket
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Weight of Jewels (g)
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Tare Wt (g)
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Extra Tare (g)
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Total Wt (g)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {floorRows.map((f, idx) => (
                  <TableRow
                    key={idx}
                    hover
                    sx={{ "&:hover": { bgcolor: BLUE_PALE } }}
                  >
                    <TableCell sx={{ ...cellSx, fontWeight: 700, color: INK }}>
                      {f.name}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {f.count}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {fmtWeight(f.gross)}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {fmtWeight(f.tare)}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      —
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...cellSx, fontWeight: 600 }}
                    >
                      {fmtWeight(f.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {!hideTotal && (
                <TableFooter>
                  <TableRow>
                    <TableCell sx={footBase}>Total</TableCell>
                    <TableCell align="right" sx={footBase}>
                      {floorTotals.count}
                    </TableCell>
                    <TableCell align="right" sx={footBase}>
                      {fmtWeight(floorTotals.gross)}
                    </TableCell>
                    <TableCell align="right" sx={footBase}>
                      {fmtWeight(floorTotals.tare)}
                    </TableCell>
                    <TableCell align="right" sx={footBase}>
                      —
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...footBase, color: SUCCESS }}
                    >
                      {fmtWeight(floorTotals.total)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        ) : (
          /* ── Default list with dynamic columns ── */
          <TableContainer sx={tableContainerSx}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={numericTypes.has(col.type) ? "right" : "left"}
                      sx={headCellSx}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ "&:hover": { bgcolor: BLUE_PALE } }}
                  >
                    {columns.map((col) =>
                      col.type === "address" ? (
                        <TableCell
                          key={col.key}
                          sx={{
                            ...cellSx,
                            maxWidth: 200,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: INK_MUTED,
                          }}
                        >
                          <Tooltip title={r.address || ""}>
                            <span>{r.address}</span>
                          </Tooltip>
                        </TableCell>
                      ) : (
                        <TableCell
                          key={col.key}
                          align={numericTypes.has(col.type) ? "right" : "left"}
                          sx={{
                            ...cellSx,
                            whiteSpace:
                              col.type === "date" || col.type === "text"
                                ? "nowrap"
                                : undefined,
                            ...(col.strong
                              ? { fontWeight: 700, color: INK }
                              : {}),
                          }}
                        >
                          {formatCell(col, r)}
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                ))}
              </TableBody>
              {!hideTotal && (
                <TableFooter>
                  <TableRow>
                    {columns.map((col, i) => (
                      <TableCell
                        key={col.key}
                        align={numericTypes.has(col.type) ? "right" : "left"}
                        sx={{
                          ...footBase,
                          ...(col.total ? { color: SUCCESS } : {}),
                        }}
                      >
                        {col.total
                          ? fmtINR(totals[col.total])
                          : i === 0
                            ? "Totals"
                            : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        )}
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettlementReport;
